// @ts-strict-ignore
import { MediaList } from "./components/MediaList";
import { Nav, regexIsNodeContentScene } from "./components/Nav";
import { MediaDetailContainer } from "./components/MediaDetail";
import { Actions } from "./components/Actions";
import { createContext, useContext, useEffect, useRef, useState, useMemo } from "react";
import { shallowEqual } from "react-redux";
import type { AppDispatch, RootState } from "../../common/state/store";
import type { IMedia } from "../../model/unityObject";
import {
  setCurrentMediaSidebar,
  getCurrentMediaGalleryId,
  getOriginalMediaGalleryId,
  setCurrentMediaGallery,
  getUploadedFile,
  setUploadedFile,
  setUploadedOldFile,
  getCurrentModeListGallery,
  fetchMediaSearchIndex,
  getFilteredMedias,
  fetchFilteredMedias,
  getMediaSearch,
  getMediaSearchIndex,
} from "./state/gallerySlice";
import { currentOrgSelector } from "../../common/state/selectors/authSelector";
import {
  closeConfirmationModal,
  closeSlider,
  openConfirmationModal,
  openSlider,
} from "../../common/state/slice/modal/modalSlice";
import ModalConfirmation from "../../common/components/Modal/ModalConfirmation";
import { useTranslation } from "react-i18next";
import { GenerateMediaPanel } from "./components/GenerateMediaPanel";
import { UploadPanel, UPLOAD_SLIDEOVER_COMPONENT_ID } from "./components/UploadPanel";
import { getNodeById } from "../graph/state/graphLegacy";
import { useLocation } from "react-router-dom";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import {
  computeUploadPanelParams,
  deduceMediaType,
  fromMediaTypeInferInputAcceptParam,
} from "./state/uploadUtil";
import { setAlert } from "../alert/state/alertsSlice";
import { FolderDownloadIcon } from "@heroicons/react/outline";
import { PaginationControls } from "src/common/components/PaginationControls/PaginationControls";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { IsLoading, useLoading } from "src/common/components/AppState/IsLoading";
import { useGalleryPagination } from "./useGalleryPagination";
import { isGifMedia } from "./util/mediaTypeUtil";

export const MEDIA_LIST_COMPONENT_ID = "mediaListComponent";

// True for any Gallery rendered inside another Gallery's media-generation panel.
// A nested (modal-picker) Gallery uses it to skip rendering its own
// GenerateMediaPanel, which would otherwise re-open the global generate
// slide-over on top of the picker. See the render site near <GenerateMediaPanel />.
const NestedGalleryContext = createContext(false);

export const Gallery = ({
  isMediaPickerMode = false,
  onConfirmSelectMedia,
  onCancel,
  nodeId,
  isModalEdit,
  contentType,
  title = "",
  allowedMediaTypeOverride,
  allowedMedia360TagOverride,
  isModalGallery = false,
  uploadAcceptOverride,
  onUploadedFileSelect,
}: {
  isModalEdit?: boolean;
  projectId?: string;
  // We can use the gallery to select media in graph explorer. Set this to true to do it
  isMediaPickerMode?: boolean;
  onConfirmSelectMedia?: (media: IMedia) => void;
  onCancel?: () => void;
  nodeId?: string;
  contentType?: string;
  title?: string;
  allowedMediaTypeOverride?: string;
  allowedMedia360TagOverride?: string;
  isModalGallery?: boolean;
  uploadAcceptOverride?: string;
  // When set, uploads (Nav button and drag-and-drop) hand the raw file to the
  // caller instead of opening the DB-backed upload slide-over — required for a
  // nested picker, whose host generate slide-over shares that global slot.
  onUploadedFileSelect?: (file: File) => void;
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useAppDispatch();
  const handlePageChange = useGalleryPagination();
  const { pathname } = useLocation();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);

  const nodeTypeScene = useAppSelector((state: RootState) =>
    getNodeById(state, String(nodeId), contentType),
  );

  // SELECTOR SYNCHRONIZATION:
  // sourceMediasIndex (from getMediaSearchIndex) and filteredMedias (from getFilteredMedias) are
  // separate Redux slices but stay synchronized because:
  // 1. Both use the same search parameters from state.media.search (page, limit, query, filters)
  // 2. Both are fetched on mount and when search params change
  // 3. searchIndex provides lightweight IDs for pagination count
  // 4. filteredMedias provides full objects for current page display
  // Note: Any changes to search params trigger both selectors to update via their respective actions
  const sourceMediasIndex = useAppSelector(getMediaSearchIndex);
  const { page, limit } = useAppSelector(getMediaSearch);

  // For content-scene nodes, filter out GIF files from the count to match displayed media
  // This fixes the pagination discrepancy where GIFs are counted but not shown
  //
  // WHY GIFs REACH THE GALLERY:
  // - Backend API returns ALL organization media (not filtered by context)
  // - Upload restrictions (uploadUtil.ts:203 IMAGE_TYPES_CONTENT_SCENES) prevent NEW GIF uploads for scenes
  // - But GIFs can exist in media library from: (1) legacy data, (2) uploads from other contexts (profile/org)
  // - Media is organization-wide and reusable across projects/contexts
  // - Frontend is responsible for context-specific filtering based on node type
  //
  // useAppSelector must run unconditionally (Rules of Hooks); the nodeId guard
  // lives inside the selector instead of around the hook call.
  const currentNode = useAppSelector((state: RootState) =>
    Number(nodeId) !== 0 ? getNodeById(state, nodeId ? nodeId : "-1") : null,
  );
  const isNodeContentScene = currentNode?.modelName === "content-scene";
  const isContentSceneContext = regexIsNodeContentScene(pathname) || isNodeContentScene;

  // Memoize filtered media index to avoid recalculating on every render
  // Only recalculates when sourceMediasIndex or isContentSceneContext changes

  // isGifMedia is a stable imported function and doesn't need to be in deps
  const filteredSourceMediasIndex = useMemo(
    () =>
      isContentSceneContext
        ? sourceMediasIndex.filter((media) => !isGifMedia(media))
        : sourceMediasIndex,
    [sourceMediasIndex, isContentSceneContext],
  );

  const totalSourceMedias = filteredSourceMediasIndex.length;

  const totalPages = Math.max(1, Math.ceil(totalSourceMedias / limit));

  const Modal_ID = "media-detail-slideover";

  const [isDragging, setIsDragging] = useState(false);
  const [opacity, setOpacity] = useState("opacity-0");

  const { filteredMedias, originalMediaGalleryId, currentMediaGalleryId, org } = useAppSelector(
    (state: RootState) => {
      return {
        filteredMedias: getFilteredMedias(state), // use this to get all medias
        // NB: do not reintroduce mediaByProjectSelector here. It was computed
        // but never destructured, and being uncached it returned a fresh array
        // on every dispatch — defeating shallowEqual and re-rendering the whole
        // Gallery on each upload-progress tick.
        originalMediaGalleryId: getOriginalMediaGalleryId(state),
        currentMediaGalleryId: getCurrentMediaGalleryId(state),
        org: currentOrgSelector(state),
      };
    },
    shallowEqual,
  );

  useEffect(() => {
    // org?.id is undefined on the first render and only resolves once the org
    // loads into Redux. Firing here on the undefined→id transition means we
    // fetch the list twice (throwaway first fire, then the real one) and repaint
    // the loading state twice. Skip until org is known so we fetch exactly once.
    if (org?.id === undefined) return;
    void dispatch(fetchFilteredMedias({ limit: 20, componentId: MEDIA_LIST_COMPONENT_ID }));
  }, [org?.id]);

  const currentModeListGallery = useAppSelector(getCurrentModeListGallery);
  const uploading = useAppSelector(getUploadedFile);
  // Set by an ancestor Gallery's generate panel; true means we are a nested
  // (modal picker) Gallery and must not spawn our own GenerateMediaPanel.
  const isNestedInGallery = useContext(NestedGalleryContext);
  const currentMediaSearch = useAppSelector(getMediaSearch, shallowEqual) as any;
  const galleryLoadingState = useLoading(MEDIA_LIST_COMPONENT_ID);
  const isGalleryLoading = galleryLoadingState < 2;

  const openMediaDetail = (mediaId: number) => {
    dispatch(openSlider(Modal_ID));
    dispatch(setCurrentMediaSidebar(mediaId));
  };

  // showSlider is a single global slot shared by every slide-over. A modal
  // picker nested inside another Gallery (the 2D→360 "Ouvrir la galerie" flow)
  // must NOT write to it: closing/resetting the slot would clobber the outer
  // generate slide-over that spawned this picker, unmounting the picker itself.
  // The outer (non-nested) Gallery keeps ownership of upload/reset behavior.
  const ownsGlobalSlider = !(isModalGallery && isNestedInGallery);

  useEffect(() => {
    dispatch(setUploadedFile(undefined));
    dispatch(setUploadedOldFile(undefined));
    if (ownsGlobalSlider) {
      dispatch(closeSlider(""));
    }
    // searchIndex (~155 kB) only feeds the Gallery's totalPages calc, which
    // PaginationControls IGNORES in galleryMode (it derives page count from
    // fetchFilteredMedias' totalResults instead — PaginationControls.tsx). The
    // modal picker always runs in galleryMode, so the index is dead weight here.
    // Skip it to drop a heavy request off the picker's open path.
    if (!isModalGallery) {
      void dispatch(fetchMediaSearchIndex({}));
    }
  }, [isModalGallery, ownsGlobalSlider]);

  useEffect(() => {
    if (wrapperRef && wrapperRef.current) {
      wrapperRef.current.scrollIntoView();
    }
  }, [currentMediaSearch]);

  useEffect(() => {
    dispatch(setCurrentMediaGallery(originalMediaGalleryId));

    const sc = () => {
      const el = document?.getElementById("gallery-selected-media-target");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } else {
        setTimeout(sc, 100);
      }
    };

    if (nodeId) {
      sc();
    }
  }, [originalMediaGalleryId]);

  const onCancelWrapper = () => {
    if (Number(originalMediaGalleryId) !== Number(currentMediaGalleryId)) {
      dispatch(openConfirmationModal("confirmCloseGallery"));
    } else {
      if (onCancel) {
        dispatch(closeConfirmationModal("confirmCloseGallery"));
        onCancel();
      }
    }
  };

  useEffect(() => {
    // Nested modal picker: leave the shared slide-over slot alone (see
    // ownsGlobalSlider). The outer Gallery drives the upload slide-over.
    if (!ownsGlobalSlider) return;
    if (uploading) {
      dispatch(openSlider(UPLOAD_SLIDEOVER_COMPONENT_ID));
    } else {
      dispatch(closeSlider(UPLOAD_SLIDEOVER_COMPONENT_ID));
    }
  }, [uploading, ownsGlobalSlider]);

  /**
   * Filters media based on current context and upload status
   * For content-scene contexts: Excludes GIFs and transcoding media
   * For other contexts: Excludes only transcoding media
   * @returns Filtered array of displayable media
   */
  const getFilteredMediaForContext = () => {
    if (isContentSceneContext) {
      return filteredMedias.filter(
        (media: IMedia) => media.upload_status !== "transcoding" && !isGifMedia(media),
      );
    } else {
      return filteredMedias.filter((media: IMedia) => media.upload_status !== "transcoding");
    }
  };

  // drag mode
  const dynamicWarningDragAndDrop = () => {
    if (!nodeId) {
      return t("general.extensionNotAccepted");
    }

    switch (nodeTypeScene?.modelName) {
      case "content-video-2-d":
        return t("general.extensionNotAcceptedVideo2d");
      case "content-audio":
        return t("general.extensionNotAcceptedAudio");
      case "content-scene":
        return currentMediaSearch?.mediaType === "model3d"
          ? t("general.extensionNotAccepted3d")
          : t("general.extensionNotAcceptedImageAndVideo");
      case "content-thumbnail":
      case "content-onboarding":
        return t("general.extensionNotAcceptedImage");
      case "content-3d-viewer":
      case "content-3-d-viewer":
        return t("general.extensionNotAccepted3d");
      default:
        return t("general.extensionNotAccepted");
    }
  };

  const alertExtension = (e: any) => {
    dispatch(
      setAlert({
        msg: dynamicWarningDragAndDrop(),
        type: "warning",
        isOpen: true,
        customTimeoutInMs: 20000,
      }),
    );
    e.dataTransfer.files[0] = null;
  };

  let selectedFileType = "all";
  if (currentNode) {
    if (currentNode.modelName === "content-scene" && currentMediaSearch?.mediaType !== "model3d") {
      selectedFileType = "content-scene";
    } else {
      selectedFileType = currentMediaSearch?.mediaType;
    }
  }
  if (regexIsNodeContentScene(pathname)) {
    selectedFileType = "content-scene";
  }

  const isNodeContentSceneOrRegex = isNodeContentScene || regexIsNodeContentScene(pathname);

  // Distinguish OS file drags from internal element drags (e.g. gallery thumbnails)
  const isExternalFileDrag = (event: React.DragEvent) => {
    if (!event.dataTransfer) return false;
    return Array.prototype.some.call(
      event.dataTransfer.types,
      (type: string) => type === "Files" || type === "application/x-moz-file",
    );
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isExternalFileDrag(event)) return;
    setTimeout(() => {
      setOpacity("opacity-1");
    }, 300);
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpacity("opacity-0");
    setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (!isExternalFileDrag(event)) return;
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    setOpacity("opacity-0");

    if (!event.dataTransfer?.files?.length) return;

    const file = event.dataTransfer.files[0];
    const extension = file?.name.slice(file?.name.lastIndexOf("."));

    // Use isGifMedia helper for consistent GIF detection
    const testIfExtensionIsGif = () => {
      if (isNodeContentSceneOrRegex) {
        return isGifMedia({ type: file?.type, filename: file?.name });
      }
      return false;
    };

    let currentFileType = event.dataTransfer.files[0].type;
    if (isStringNullOrEmpty(currentFileType)) {
      const splitFileName = event.dataTransfer.files[0].name.split(".");
      if (splitFileName.length > 0) {
        try {
          const type = deduceMediaType(splitFileName[splitFileName.length - 1]);
          currentFileType = type + "/" + splitFileName[splitFileName.length - 1];
        } catch {
          currentFileType = "";
        }
      }
    }

    const splitInArray = (
      uploadAcceptOverride ?? fromMediaTypeInferInputAcceptParam(selectedFileType)
    ).split(",");
    const testExtensions = !splitInArray.includes(currentFileType);

    if (testExtensions || extension === ".m4v" || testIfExtensionIsGif()) {
      alertExtension(event);
    } else if (onUploadedFileSelect) {
      onUploadedFileSelect(event.dataTransfer.files[0] as File);
    } else {
      const uploadedFile = event.dataTransfer.files[0] as File;
      const cupp = computeUploadPanelParams({ file: uploadedFile, from: "graph" });
      dispatch(setUploadedFile({ file: uploadedFile, metadata: cupp }));
    }
  };

  return (
    <div>
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`absolute block flex w-full h-full ${
          isDragging
            ? `dragging bg-white ${opacity} ease-in-out duration-[900ms] border-2 border-green-500`
            : ""
        }`}
      >
        {/* hierarchy problem with divs and the draging is disabled when colliding with other divs */}
        {isDragging ? (
          <div className="pointer-events-none w-ful h-full mx-auto">
            <div className="pointer-events-none italic text-5xl text-white flex flex-col items-center mt-96 font-bold z-[999] text-gray-500 ">
              {t("general.dragFilesHere")}
              <div className="text-xl mt-2 text-gray-500">{t("general.dragAndDropText")}</div>

              <FolderDownloadIcon className="w-20 h-20 stroke-green-500 mt-6" />
            </div>
          </div>
        ) : (
          <div ref={wrapperRef} className="flex w-full h-full">
            <div className="w-full gallery h-full flex flex-col">
              <div ref={navbarRef}>
                <Nav
                  title={title}
                  nodeId={nodeId ?? "0"}
                  allowedMediaTypeOverride={allowedMediaTypeOverride}
                  allowedMedia360TagOverride={allowedMedia360TagOverride}
                  hideGenerateButton={!ownsGlobalSlider}
                  uploadAcceptOverride={uploadAcceptOverride}
                  onUploadedFileSelect={onUploadedFileSelect}
                  isModalGallery={isModalGallery}
                />
              </div>

              <div
                className={`flex flex-col flex-grow overflow-y-auto h-full  mediaListWrapper ${
                  currentModeListGallery && !isModalGallery ? "p-2" : ""
                }`}
              >
                <IsLoading componentId={MEDIA_LIST_COMPONENT_ID} showSuccess={false}>
                  <MediaList
                    medias={getFilteredMediaForContext()}
                    isModalEdit={isModalEdit}
                    listMode={isModalGallery ? false : currentModeListGallery}
                    openMediaDetail={openMediaDetail}
                    currentMedia={currentMediaGalleryId}
                    isMediaPickerMode={isMediaPickerMode}
                  />
                </IsLoading>
              </div>

              <ModalConfirmation
                title={t("pages.gallery.cancelConfirmationModal.title")}
                text={t("pages.gallery.cancelConfirmationModal.body")}
                onClickSubmit={() => {
                  dispatch(closeConfirmationModal("confirmCloseGallery"));
                  if (onCancel) {
                    onCancel();
                  }
                }}
                id={"confirmCloseGallery"}
              />

              <IsLoading componentId={MEDIA_LIST_COMPONENT_ID} showSuccess={false}>
                <div className="h-[4rem] w-full border-t ">
                  <PaginationControls
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    isLoading={isGalleryLoading}
                    className="w-[97%] self-center"
                    galleryMode
                  />
                </div>
              </IsLoading>

              {onConfirmSelectMedia && (
                <Actions onConfirmSelectMedia={onConfirmSelectMedia} onCancel={onCancelWrapper} />
              )}
            </div>

            <MediaDetailContainer id={Modal_ID} large={false} />
            <UploadPanel uploadFromSelectionCb={onConfirmSelectMedia} />
            {/* A Gallery owns the media-generation slide-over. A modal-picker
                Gallery launched from inside another Gallery's generate panel
                (e.g. the 2D→360 "Ouvrir la galerie" flow) must NOT render a
                second one: the generate slide-over is a global singleton, so the
                nested copy would re-open on top of the picker and bury its nav,
                selection outline and confirm footer. Skip it only in that nested
                case; provide the flag so the picker's own Gallery knows to skip. */}
            {!(isModalGallery && isNestedInGallery) && (
              <NestedGalleryContext.Provider value={true}>
                <GenerateMediaPanel />
              </NestedGalleryContext.Provider>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
